import { useEffect } from 'react';
import {
  useWatch,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from 'react-hook-form';

import {
  useSuggestedInvoiceNumber,
  type SuggestedInvoiceNumberParams,
} from '../api/use-suggested-invoice-number';

// Prefills the invoice-number field with the server suggestion once it arrives —
// but never over input the operator (or an AI-extraction prefill) already put
// there: only an empty, untouched field is filled. Call after the dialog's own
// form hook so the open-reset effect runs first.
//
// `placeholder` extends "empty" for forms that must pre-seed the field (a
// hidden auto-valued number, where '' would block submit before the suggestion
// resolves): an untouched field still holding exactly that fallback value is
// upgraded to the suggestion too.
//
// The live field value is a dependency on purpose: the dialogs re-run
// `form.reset(defaults)` whenever their defaults' identity changes (parent
// queries resolving/refetching while the dialog is open), which wipes the
// filled suggestion back to ''. Watching the value re-triggers the fill after
// such a reset; the dirty check still keeps operator-typed numbers untouched.
export function useSuggestedNumberPrefill<T extends FieldValues>(
  form: UseFormReturn<T>,
  field: Path<T>,
  params: SuggestedInvoiceNumberParams,
  placeholder?: string,
): void {
  const { data } = useSuggestedInvoiceNumber(params);
  const enabled = params.enabled;
  const liveValue = useWatch({ control: form.control, name: field });
  useEffect(() => {
    if (data === undefined || !enabled) return;
    const current: unknown = form.getValues(field);
    const { isDirty } = form.getFieldState(field);
    const replaceable = current === '' || (placeholder !== undefined && current === placeholder);
    if (replaceable && !isDirty) {
      // The field is a string field on every consuming form; PathValue can't
      // prove that across the generic, hence the narrow assignment cast.
      form.setValue(field, data.invoice_number as PathValue<T, Path<T>>);
    }
  }, [data, enabled, form, field, liveValue, placeholder]);
}
